import { FiboSearchHeadline, FiboSearchType, TaxonomyType } from '../../../constants';
import type { FiboProductItem, FiboSuggestionItem, FiboTaxonomyItem } from '../../../model';
import type { AutocompleteGroupItem, AutocompleteProductItem, FiboSearchResponse, SearchAutocompleteResponse } from './types';

const toFiboTaxonomyItem = (item: AutocompleteGroupItem, taxonomy: TaxonomyType): FiboTaxonomyItem => ({
  termId: item.id,
  taxonomy,
  value: item.value,
  url: item.url,
  imageSrc: '',
  breadcrumbs: item.breadcrumbs ?? '',
  count: '',
  type: FiboSearchType.TAXONOMY,
  score: item.score ?? 0,
});

// Crawled marketing hashtag strings stored as single tags; hide until the backend index excludes them.
const isJunkHashtagTag = (item: AutocompleteGroupItem): boolean => item.value.startsWith('#');

const toFiboProductItem = (item: AutocompleteProductItem): FiboProductItem => ({
  postId: item.productId,
  value: item.name,
  url: item.url,
  thumbHtml: item.imageUrl ? `<img src="${item.imageUrl}">` : '',
  price: '',
  desc: '',
  sku: item.sku,
  onSale: item.onSale,
  featured: false,
  type: FiboSearchType.PRODUCT,
});

/**
 * Adapts the Meilisearch autocomplete response (server-grouped) to the legacy
 * FiboSearch flat suggestion list (headline markers followed by group items),
 * so existing combobox UIs render unchanged.
 */
export const toFiboSearchResponse = (response: SearchAutocompleteResponse): FiboSearchResponse => {
  const suggestions: FiboSuggestionItem[] = [];

  const appendGroup = (headline: FiboSearchHeadline, items: FiboSuggestionItem[]) => {
    if (items.length === 0) return;
    suggestions.push({ value: headline, type: FiboSearchType.HEADLINE }, ...items);
  };

  appendGroup(
    FiboSearchHeadline.TAX_BRAND,
    response.groups.brands.map((item) => toFiboTaxonomyItem(item, TaxonomyType.BRAND)),
  );
  appendGroup(
    FiboSearchHeadline.TAX_PRODUCT_CATEGORY,
    response.groups.categories.map((item) => toFiboTaxonomyItem(item, TaxonomyType.PRODUCT_CATEGORY)),
  );
  appendGroup(
    FiboSearchHeadline.TAX_PRODUCT_TAG,
    response.groups.tags
      .filter((item) => !isJunkHashtagTag(item))
      .map((item) => toFiboTaxonomyItem(item, TaxonomyType.PRODUCT_TAG)),
  );
  appendGroup(FiboSearchHeadline.PRODUCT, response.groups.products.map(toFiboProductItem));

  const itemCount = suggestions.filter((item) => item.type !== FiboSearchType.HEADLINE).length;

  return {
    suggestions,
    total: itemCount,
    tntTime: '',
    time: '',
    engine: 'meilisearch',
    v: '',
  };
};
